import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    FluentProvider,
    Input,
    Label,
    makeStyles,
    mergeClasses,
    useId,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useTakeSnapshotDialogContext } from '../../../hooks/context/dialogs';
import { useCurrentFluent2Theme } from '../../../hooks/styling';
import { DevBoxSnapshot } from '../../../models/dev-box';
import { useStackStyles } from '../../../themes/styles/flexbox-styles';
import { getNumericDateStringFromDate, getNumericTimeStringFromDate } from '../../../utilities/time';
import ReplaceSnapshotDialog from '../dev-box-card/replace-snapshot-dialog/replace-snapshot-dialog';
import { SnapshotType } from '../restore-snapshot-dialog/model';

export interface TakeSnapshotDialogProps {
    onDismiss: () => void;
    onTakeSnapshotSubmitted: () => void;
    open?: boolean;
    locale: string;
    snapshots?: DevBoxSnapshot[];
}

const messages = defineMessages({
    takeSnapshotButtonAriaLabel: {
        id: 'TakeSnapshotDialog_Button_AriaLabel',
        defaultMessage: 'Take Snapshot',
        description: 'Aria label for the submit button on the Take Snapshot dialog',
    },
    cancelButtonAriaLabel: {
        id: 'TakeSnapshotDialog_CancelButton_AriaLabel',
        defaultMessage: 'Cancel',
        description: 'Aria label for the cancel button on the Take Snapshot dialog.',
    },
    closeButtonAriaLabel: {
        id: 'TakeSnapshotDialog_CloseButton_Text',
        defaultMessage: 'Close',
        description: 'Aria label for the "x" button in the top corner of the TakeSnapshot dialog',
    },
});

/**
 * Styles
 */
const dialogWidth = '520px';
const dialogStyle: React.CSSProperties = {
    width: dialogWidth,
};

const useDialogStackStyles = makeStyles({
    root: {
        fontWeight: 'normal',
        gap: '18px',
        padding: '0px 0px 30px 0px',
    },
});

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
        maxWidth: '400px',
    },
});

/**
 * End Styles
 */

const TakeSnapshotDialog: React.FC<TakeSnapshotDialogProps> = (props: TakeSnapshotDialogProps) => {
    const { onDismiss, onTakeSnapshotSubmitted, open, locale, snapshots = [] } = props;
    const snapshotId = useId('input');
    const [value] = React.useState(
        new Date()
            .toLocaleString(locale || 'en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            })
            .replace(',', ' at')
    );

    const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

    // Theming hooks
    const theme = useCurrentFluent2Theme();

    // Intl hooks
    const { formatMessage } = useIntl();

    // Style hooks
    const stackStyles = useStackStyles();
    const dialogStackStyles = useDialogStackStyles();
    const styles = useStyles();

    const onConfirmSnapshot = React.useCallback(() => {
        onTakeSnapshotSubmitted();
        onDismiss();
    }, [onTakeSnapshotSubmitted, onDismiss]);

    const manualSnapshot = React.useMemo(() => {
        return snapshots.find((snapshot) => snapshot.snapshotType === SnapshotType.Manual);
    }, [snapshots]);

    const manualSnapshotName = React.useMemo(() => {
        if (!manualSnapshot) {
            return '';
        }

        const createdTime = manualSnapshot.createdTime;
        const dateString = getNumericDateStringFromDate(createdTime, locale);
        const timeString = getNumericTimeStringFromDate(createdTime, locale);

        return formatMessage(
            {
                id: 'DateTime_Format',
                defaultMessage: '{dateString} at {timeString}',
                description: 'Format for displaying date and time',
            },
            { dateString, timeString }
        );
    }, [manualSnapshot]);

    // Check if a manual snapshot exists
    const hasManualSnapshot = !!manualSnapshot;

    const onTakeSnapshotClick = React.useCallback(() => {
        if (hasManualSnapshot) {
            setShowConfirmDialog(true);
        } else {
            onConfirmSnapshot();
        }
    }, [hasManualSnapshot, onConfirmSnapshot]);

    return (
        <FluentProvider theme={theme}>
            <Dialog modalType="alert" open={open} onOpenChange={onDismiss}>
                <DialogSurface style={dialogStyle}>
                    <DialogBody>
                        <DialogTitle
                            action={
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label={formatMessage(messages.closeButtonAriaLabel)}
                                        icon={<Dismiss24Regular />}
                                    />
                                </DialogTrigger>
                            }
                        >
                            <FormattedMessage
                                id="TakeSnapshotDialog_Title"
                                defaultMessage="Take snapshot?"
                                description="The title for the dialog describing the Take Snapshot."
                            />
                        </DialogTitle>
                        <DialogContent>
                            <div className={mergeClasses(stackStyles.root, dialogStackStyles.root)}>
                                <div className={stackStyles.item}>
                                    <FormattedMessage
                                        id="TakeSnapshotDialog_Description"
                                        defaultMessage="Taking a snapshot will enable you to receive the current state of your dev box as a backup."
                                        description="Take Snapshot dialog description."
                                    />
                                </div>
                                <div className={styles.root}>
                                    <Label htmlFor={snapshotId}>
                                        <FormattedMessage
                                            id="TakeSnapshotDialog_SnapshotName_Label"
                                            defaultMessage="Snapshot name"
                                            description="Label for the snapshot name input field in the Take Snapshot dialog"
                                        />
                                    </Label>
                                    <Input value={value} id={snapshotId} readOnly />
                                </div>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                appearance="primary"
                                aria-label={formatMessage(messages.takeSnapshotButtonAriaLabel)}
                                onClick={onTakeSnapshotClick}
                            >
                                <FormattedMessage
                                    id="TakeSnapshotDialog_SubmitButton_Label"
                                    defaultMessage="Take snapshot"
                                    description="Label for the submit button on the take snapshot dialog."
                                />
                            </Button>
                            <DialogTrigger>
                                <Button
                                    appearance="secondary"
                                    aria-label={formatMessage(messages.cancelButtonAriaLabel)}
                                >
                                    <FormattedMessage
                                        id="TakeSnapshotDialog_CancelButton_Label"
                                        defaultMessage="Cancel"
                                        description="Label for the cancel button on the Take Snapshot dialog."
                                    />
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            {showConfirmDialog && (
                <ReplaceSnapshotDialog
                    open={showConfirmDialog}
                    existingManualSnapshotName={manualSnapshotName}
                    newSnapshotName={value}
                    onReplaceSnapshotSubmitted={() => {
                        setShowConfirmDialog(false);
                        onTakeSnapshotSubmitted();
                    }}
                    onDismiss={() => {
                        setShowConfirmDialog(false);
                    }}
                    onCancel={() => {
                        setShowConfirmDialog(false);
                        onDismiss();
                    }}
                />
            )}
        </FluentProvider>
    );
};

export const TakeSnapshotDialogContextWrapper: React.FC = () => {
    // Context hooks
    const { isOpen, closeSurface, properties } = useTakeSnapshotDialogContext();

    if (!isOpen) {
        return <></>;
    }

    return <TakeSnapshotDialog {...properties} open={isOpen} onDismiss={closeSurface} />;
};
